// cleanup_hoc_to_hooks_LOGIN_WORKFLOW
// cleanup_convert_to_typescript_file_LOGIN_WORKFLOW

import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, PRIMARY, SECONDARY, TERTIARY } from '@glu/buttons-react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import Alert from '@glu/alerts-react';
import NavigationPanel from '@glu/steps-react';
import { withFormProvider, FormSection } from '@glu/validation-react';

import { UserShape, UserDefaultProps } from 'api/user/props';
import ChallengeManager from 'components/challengeManager/ChallengeManager';
import ModalNotification from 'components/modalNotification/ModalNotification';
import Workflow from 'components/workflow/Workflow';
import useLocale from 'components/useLocale/useLocale';
import {
  FIELD_NAMES,
  NEW_PASSWORD,
  QUESTION,
  SECURITY_CONTACTS,
  SECURITY_QUESTIONS,
  SUBMIT_TYPES
} from 'components/securityContact/securityContacts.constants';
import { ALERTVARIATIONS, GLU_MODAL_TYPES, WORKSPACES } from 'constants.js';
import {
  logout as logoutAction,
  setCurrentUser as setCurrentUserAction
} from 'logic/store/authentication/actions';
import { clearGrids as clearGridsAction } from 'logic/store/grids/actions';

import FirstTimeCreateNewPassword from './FirstTimeCreateNewPassword';
import FirstTimeSecurityContacts from './FirstTimeSecurityContacts';
import runOnSubmit, { loadData } from './firstTimeLogin.api';
import useStyles from './firstTimeLogin.styles';
import SecurityQuestionsForm from './SecurityQuestionsForm';

const FirstTimeLoginFlow = ({ data: values, errors, onChange, user, validate }) => {
  const [APIData, setAPIData] = useState(null);
  const [APIErrors, setAPIErrors] = useState(null);
  const [loadedTheme, setLoadedTheme] = useState(false);
  const [contactPoint, setContactPoint] = useState({ sendingCode: false });
  const [confirmation, setConfirmation] = useState(null);
  const [challengeManager, setChallengeManager] = useState(null);
  const [message, setMessage] = useState(null);
  const classes = useStyles();
  const locale = useLocale();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // Load data and state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    loadData({
      onChange,
      setAPIData,
      setAPIErrors,
      setContactPoint,
      setLoadedTheme,
      user
    }),
    []
  );

  const securityValues = values.security || {};
  // Only allow the user to use question once
  const availableQuestionsToPickFrom =
    APIData && APIData.questions
      ? APIData.questions.filter((question) => {
          const selectedQuestions = [
            securityValues?.securityQuestion1?.[0]?.id,
            securityValues?.securityQuestion2?.[0]?.id,
            securityValues?.securityQuestion3?.[0]?.id
          ];
          return !selectedQuestions.includes(question.id);
        })
      : [];

  const currentStep = location.hash || SUBMIT_TYPES.SECURITY_QUESTIONS;
  const completedSecurityContacts = contactPoint.completed;

  const setCurrentUser = (...params) => dispatch(setCurrentUserAction(...params));
  const logout = (...params) => dispatch(logoutAction(...params));
  const clearGrids = () => clearGridsAction(dispatch);

  const onSubmit = (submitType) => {
    return runOnSubmit({
      APIData,
      confirmation,
      contactPoint,
      history,
      locale,
      logout,
      onChange,
      setChallengeManager,
      setConfirmation,
      setContactPoint,
      setCurrentUser,
      setMessage,
      submitType,
      user,
      values
    });
  };

  const onClickNext = async () => {
    const { isValid } = await validate();
    // If the mobile contact UI card is open and is not verified then show error
    // Only use this error when on #securityContacts section
    if (currentStep === SUBMIT_TYPES.SECURITY_CONTACTS) {
      if (securityValues.mobileContact && !contactPoint.contactPointReadyToBeAdded) {
        return setContactPoint((state) => ({
          ...state,
          error: locale['security.contact.sms.verifyError']
        }));
      }
      if (
        !securityValues.includeEmail &&
        !securityValues.makeDefault &&
        !securityValues.makeVoiceDefault
      ) {
        return setContactPoint((state) => ({
          ...state,
          error: locale['security.contact.makeDefaultError']
        }));
      }
      setContactPoint((state) => ({
        ...state,
        error: ''
      }));
    }
    if (isValid) return onSubmit(currentStep);
    return null;
  };

  const onClickBack = () => {
    // Only show this button after first step. If on second step go to first else go to second step
    if (currentStep === SUBMIT_TYPES.SECURITY_CONTACTS) {
      return history.push(`${WORKSPACES.DASHBOARD}${SUBMIT_TYPES.SECURITY_QUESTIONS}`);
    }
    if (currentStep === SUBMIT_TYPES.NEW_PASSWORD) {
      if (user.isMfaEnabled)
        return history.push(`${WORKSPACES.DASHBOARD}${SUBMIT_TYPES.SECURITY_CONTACTS}`);
      return history.push(`${WORKSPACES.DASHBOARD}${SUBMIT_TYPES.SECURITY_QUESTIONS}`);
    }
    return null;
  };

  return (
    <Workflow
      title={locale['profile.setup']}
      returnUrl={false}
      className={classnames(classes.intro, { [classes.loaded]: loadedTheme })}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        {challengeManager && (
          // Force ChallengeManager to be above the ModalNotifaction
          <div className={classes.challengeManagerContainer}>
            <ChallengeManager
              challengeErrorMessage={Boolean(challengeManager.data.numberOfFailedAttempts)}
              data={challengeManager.data}
              open
              onCancel={() => setChallengeManager(null)}
              onHide={() => setChallengeManager(null)}
              onSubmit={() => challengeManager.callback()}
            />
          </div>
        )}
        {confirmation && (
          <ModalNotification
            variant={GLU_MODAL_TYPES.ERROR.toLowerCase()}
            onHide={() => setConfirmation(null)}
            suppressIcon={false}
            data-qa="action-modal"
            show
          >
            <confirmation.component />
          </ModalNotification>
        )}
        <div className={classes.loginFlowContainer}>
          <div className={classnames(classes.wrapper, classes.verticalGap)}>
            <NavigationPanel
              // TODO: Set vertical property based on window size listener
              // vertical
              data={[
                {
                  id: SUBMIT_TYPES.SECURITY_QUESTIONS,
                  name: SECURITY_QUESTIONS,
                  passed:
                    // Check if user filled in all fields
                    Object.keys(securityValues).filter((key) => key.includes(QUESTION)).length ===
                      6 ||
                    // If user has already answered questions then this section is passed
                    (APIData && APIData.hasQuestions && !securityValues.updatingSecurityQuestions),
                  currentStep: currentStep === SUBMIT_TYPES.SECURITY_QUESTIONS
                },
                ...(user.isMfaEnabled
                  ? [
                      {
                        id: SUBMIT_TYPES.SECURITY_CONTACTS,
                        name: SECURITY_CONTACTS,
                        passed: completedSecurityContacts,
                        currentStep: currentStep === SUBMIT_TYPES.SECURITY_CONTACTS
                      }
                    ]
                  : []),
                {
                  id: SUBMIT_TYPES.NEW_PASSWORD,
                  name: NEW_PASSWORD,
                  passed: false,
                  currentStep: currentStep === SUBMIT_TYPES.NEW_PASSWORD
                }
              ]}
              rootValue="name"
              positiveNegativeValue="passed"
              DataDetails={({ record: { name } }) => name}
              actionable
              numberIdentifier={false}
              withIdentifier={false}
              // This is used by the NavigationPanel to find which property to check for whether the styled bullet shows or not
              currentStep="currentStep"
              idField="id"
            />
          </div>
          {!APIData ? (
            <div className={classes.verticalGap}>
              {APIErrors ? locale['common.server.error'] : <h2>{locale['common.loading']}</h2>}
            </div>
          ) : (
            <div className={classes.verticalGap}>
              {message && (
                <Alert
                  dataQa="action-error"
                  variant={message.success ? ALERTVARIATIONS.SUCCESS : ALERTVARIATIONS.ERROR}
                  onHide={() => setMessage(null)}
                  className={classes.commonAlert}
                  message={message.title}
                />
              )}
              <FormSection name={FIELD_NAMES.SECURITY}>
                {currentStep === SUBMIT_TYPES.SECURITY_QUESTIONS && (
                  <SecurityQuestionsForm
                    availableQuestionsToPickFrom={availableQuestionsToPickFrom}
                    onChange={onChange}
                    values={values}
                  />
                )}
                {currentStep === SUBMIT_TYPES.SECURITY_CONTACTS && (
                  <FirstTimeSecurityContacts
                    classes={classes}
                    contactPoint={contactPoint}
                    onChange={onChange}
                    onSubmit={onSubmit}
                    setContactPoint={setContactPoint}
                    validate={validate}
                    values={values}
                    user={user}
                  />
                )}
                {currentStep === SUBMIT_TYPES.NEW_PASSWORD && (
                  <FirstTimeCreateNewPassword
                    APIData={APIData}
                    classes={classes}
                    errors={errors}
                    locale={locale}
                    user={user}
                    validate={validate}
                    values={values}
                  />
                )}
              </FormSection>
            </div>
          )}
          <div className={classes.wrapper}>
            {currentStep !== SUBMIT_TYPES.NEW_PASSWORD ? (
              <Button
                className="btn btn-primary"
                variant={PRIMARY}
                data-qa="next"
                onClick={onClickNext}
              >
                {locale['common.next']}
              </Button>
            ) : (
              <Button
                className="btn btn-primary"
                variant={PRIMARY}
                data-qa="complete"
                // Final submission
                onClick={async () => {
                  const { isValid } = await validate();
                  if (isValid) {
                    onSubmit(SUBMIT_TYPES.COMPLETE);
                  }
                }}
              >
                {locale['common.complete']}
              </Button>
            )}
            {currentStep !== SUBMIT_TYPES.SECURITY_QUESTIONS && (
              <Button
                className="btn btn-secondary"
                variant={SECONDARY}
                data-qa="back"
                onClick={onClickBack}
              >
                {locale['common.back']}
              </Button>
            )}
            <Button
              className="btn btn-tertiary"
              variant={TERTIARY}
              data-qa="cancel"
              onClick={() => {
                clearGrids();
                onSubmit(SUBMIT_TYPES.LOGOUT);
              }}
            >
              {locale['common.cancel']}
            </Button>
          </div>
        </div>
      </form>
    </Workflow>
  );
};

FirstTimeLoginFlow.propTypes = {
  // TODO: give proper shape to data object
  data: PropTypes.shape({}).isRequired,
  errors: PropTypes.PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  user: UserShape,
  validate: PropTypes.func.isRequired
};

FirstTimeLoginFlow.defaultProps = {
  user: UserDefaultProps
};

export default withFormProvider(FirstTimeLoginFlow);
